import { Camera } from "../../../../SharedGL/Camera";
import { IFlight, IFlightError, IHighlightFlightSettings } from "@vmosolutions/oc-interfaces";
import { GroundEventShader } from "../GroundEvent/GroundEventShader";
import { FlightGL } from "../Flight/FlightGL";
import { GroundEventBuffer } from "../GroundEvent/GroundEventBuffer";
import { hexToIRGBA, findClosestFlightIndex } from "../../../../SharedGL/TimelineUtils";
import { getFlightAlphaValue } from "../../TimelineUtils";

interface IFlightGLAttributes {
  position: WebGLBuffer;
  color: WebGLBuffer;
}

interface IFlightErrorMap {
  [key: string]: IFlight;
}

export interface IFlightErrorFlight {
  _id: string;
  actualOff: number;
  actualOn: number;
  positions: number[];
  colors: number[];
}

export class EventErrorsGL {
  public static aircraftRowTotalHeight: number = FlightGL.aircraftRowHeight + FlightGL.aircraftRowPadding;

  public static delayedTimeMillis = 900000; // 15 minutes

  public static readonly referenceTime = new Date().getTime();

  private groundEvents: GroundEventBuffer;

  protected buffers: IFlightGLAttributes;

  private vertexCount = 0;

  private flightIndexesInRange: number[] = [0, 0];
  private flightsInRange: IFlight[] = [];

  private flightErrorFlights: IFlightErrorFlight[] = [];
  private errors: IFlightError[] = [];
  constructor(gl: WebGLRenderingContext, groundEventShader: GroundEventShader, texture: WebGLTexture) {
    // const texture = loadTexture(
    //   gl,
    //   "/assets/timeline-icons/diagonal-pattern-256.png"
    // );
    this.groundEvents = new GroundEventBuffer(gl, groundEventShader, texture);
  }

  private getFlightErrorFlight = (flightId: string, flights: IFlight[]): IFlight => {
    return flights.find((flight: IFlight) => flight._id === flightId);
  };

  public updateBuffers = (
    gl: WebGLRenderingContext,
    errors: IFlightError[],
    flights: IFlight[],
    highlightFlightSettings?: IHighlightFlightSettings
  ): void => {
    this.errors = errors;

    // iterating over an aircraft (row) so height will always be same
    const y0 = FlightGL.actualDepartureStart + FlightGL.actualDepartureHeight + 1; //start the error bar 1 pixel after the end of the actual flight
    const y = FlightGL.aircraftRowTotalHeight - 1;

    const flightErrorCache: IFlightErrorMap = {};

    this.flightErrorFlights = [];

    errors.sort((a: IFlightError, b: IFlightError) => (a.priority > b.priority ? 1 : -1));
    errors.forEach((error: IFlightError) => {
      if (flightErrorCache[error.flightId] || error.hideFromTimeline) {
        return;
      }

      const flightErrorFlight: IFlight = this.getFlightErrorFlight(error.flightId, flights);

      if (!flightErrorFlight) {
        return;
      }

      // If highlightFlights boolean is true and there are conditions passed in, set flight alpha based on those user supplied conditions;
      const errorAlpha = getFlightAlphaValue(flightErrorFlight, highlightFlightSettings);

      flightErrorCache[error.flightId] = flightErrorFlight;

      // positions
      const x0 = new Date(flightErrorFlight.actualOut).getTime() - Camera.referenceTime; //Block Time switch to Out/In
      const x = new Date(flightErrorFlight.actualIn).getTime() - Camera.referenceTime; //Block Time switch to Out/In
      const positions = [x0, y0, x, y0, x, y, x0, y0, x, y, x0, y];
      const { red, green, blue } = error.priority === "CRITICAL" ? hexToIRGBA("#ff3333") : hexToIRGBA("#EED202"); //[0.8, 0.01, 0.01];
      const colors = [
        red,
        green,
        blue,
        errorAlpha,
        red,
        green,
        blue,
        errorAlpha,
        red,
        green,
        blue,
        errorAlpha,
        red,
        green,
        blue,
        errorAlpha,
        red,
        green,
        blue,
        errorAlpha,
        red,
        green,
        blue,
        errorAlpha,
      ];

      this.flightErrorFlights.push({
        _id: flightErrorFlight._id,
        actualOff: flightErrorFlight.actualOff?.valueOf(),
        actualOn: flightErrorFlight.actualOn?.valueOf(),
        positions,
        colors,
      });
    });

    const actualFlightPositions: number[] = [];
    const actualFlightColors: number[] = [];
    this.flightErrorFlights.sort((a: IFlightErrorFlight, b: IFlightErrorFlight) => a.actualOff - b.actualOff);
    this.flightErrorFlights.forEach((val: IFlightErrorFlight) => {
      const { positions, colors } = val;
      actualFlightPositions.push(...positions);
      actualFlightColors.push(...colors);
    });

    this.flightIndexesInRange = [0, Object.keys(flightErrorCache).length - 1];
    this.groundEvents.updateBuffers(gl, actualFlightPositions, actualFlightColors);
    this.vertexCount = actualFlightPositions.length / 2;
  };

  public updateFlightErrorsInRange = (camera: Camera): number[] => {
    if (this.flightErrorFlights.length === 0) {
      return [0, 0];
    }
    const [minTime, maxTime] = camera.getXRange();

    const min = findClosestFlightIndex(this.flightErrorFlights, minTime, "actualOn");
    const max = findClosestFlightIndex(this.flightErrorFlights, maxTime, "actualOff");
    // Add a buffer of one flight on either side if possible
    this.flightIndexesInRange = [Math.max(0, min - 1), Math.min(max + 1, this.flightErrorFlights.length - 1)];
    return this.flightIndexesInRange;
  };

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    this.updateFlightErrorsInRange(camera);
    const [minIndex, maxIndex] = this.flightIndexesInRange;

    if (this.vertexCount === 0 || minIndex < 0 || maxIndex < 0) {
      return;
    }
    const [minX, maxX] = camera.getXRange();

    const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
    const scale = [xScale, 0.01666];

    const offset = minIndex * 6;
    const numVerts = (maxIndex + 1) * 6 - offset;
    if (numVerts > 0 && numVerts <= this.vertexCount) {
      this.groundEvents.draw(gl, camera, yStart, offset, numVerts, scale);
    }
  };
}
